import React, {useCallback} from 'react';
import styled, {css} from 'styled-components';
import {Link} from 'react-router-dom';

import {useModalContext} from '../../../../contexts/Modal';
import Upgrade from '../../../../components/Modal/components/Upgrade';

import {Row, FlexBox} from '../../../../components/Grid';
import {H3, H5, Label3} from '../../../../components/Typography';
import {Label2, Label4} from '@bootcamp/web/src/components/Typography/next';

import {ReactComponent as Badge} from '@bootcamp/shared/src/assets/svg/badge.svg';
import {Play} from '@styled-icons/heroicons-solid/Play';
import {Checkmark} from '@styled-icons/fluentui-system-filled/Checkmark';
import {ArrowForwardOutline} from '@styled-icons/evaicons-outline/ArrowForwardOutline';
import {Flag} from '@styled-icons/evaicons-solid/Flag';
import {DocumentArrowDown} from '@styled-icons/fluentui-system-filled/DocumentArrowDown';
import {TaskListSquareLtr} from '@styled-icons/fluentui-system-filled/TaskListSquareLtr';

import {findByRenderType, parseVideoDuration} from '@bootcamp/shared/src/util';
import { ChevronRight } from '@styled-icons/fluentui-system-filled/ChevronRight';

import ProgressBar from '@bootcamp/web/src/components/ProgressBar';
import {useAsync} from '@bootcamp/shared/src/util/hooks';


const IconArea = styled.span`
  display: flex;
  align-items: center;
  margin-left: auto;
`;
const BadgeWrapper = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 20px;
  height: 20px;
`;
const StyledBadge = styled(Badge)`
  path {
    fill: ${({theme}) => theme.colors.iconPalette.secondary};
  }
`;
const DetailText = styled(H5)`
  color: ${({theme}) => theme.colors.neutralsPalette.grey};
  margin-right: ${({theme, padded}) => padded ? '4px' : theme.layouts.spacing.m};
  margin-left: auto;
  transition: all .3s;
`;
const PlayButtonIcon = styled(Play).attrs({size: 32})`
  color: ${({theme, themePalette}) => themePalette ? themePalette.default : theme.colors.brandPalette.violet.default};
  ${({theme}) => theme.mediaQueries.mobileL} {
    width: 24px;
    height: 24px;
  }
`;
const FlagButtonIcon = styled(Flag).attrs({size: 20})`
  color: ${({theme, themePalette}) => themePalette ? themePalette.default : theme.colors.brandPalette.violet.default};
`;
const RowContainer = styled(Row)`
  align-items: center;
  min-height: 48px;

  ${({mobileOnly}) => mobileOnly && css`
    display: none;
    ${({theme}) => theme.mediaQueries.mobileL} {
      display: flex;
    }
  `}
`;
const RowLabel = styled(Label3)`
  color: ${({theme, themePalette}) => themePalette ? themePalette.dark : theme.colors.brandPalette.violet.dark};
  margin-bottom: ${({theme}) => theme.layouts.spacing.s};
  display: none;
  ${({mobileOnly}) => mobileOnly && css`
    display: none !important;
    ${({theme}) => theme.mediaQueries.mobileL} {
      margin-bottom: 0px;
      font-size: 14px;
      line-height: 14px;
    }
  `}
`;
const RowContent = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  align-items: center;
  flex: 1;
  gap: 4px;
  padding: 16px;

  ${({theme}) => theme.mediaQueries.tablet} {
    align-items: flex-start;
    flex-direction: column;
  }
`;
const RowTitle = styled(Label2)`
  color: ${({theme}) => theme.colors.typographyPalette.primary};

  ${({theme}) => theme.mediaQueries.tablet} {
    font-size: 14px;
  }
`;

const QuizDetails = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;

  ${({theme}) => theme.mediaQueries.tablet} {
    width: 100%;
  }
`;
const QuizDetailsText = styled(Label2)`\
  color: ${({theme}) => theme.colors.typographyPalette.secondary};
  opacity: ${({loading}) => loading ? 0 : 1};
  transition: opacity .2s;

  ${({theme}) => theme.mediaQueries.tablet} {
    font-size: 14px;
  }
`;

const ContentTypeChicletContainer = styled.div`
  padding: 11px 14px;
`;
const ContentTypeText = styled(Label4)`
`;
const ContentTypeChiclet = styled.div`
  padding: 4px 8px;
  border-radius: 4px;
  width: 52px;
  height: 20px;
  text-align: center;

  border: 1px solid ${({theme, type}) => type === 'lesson'
    ? `${theme.colors.brandPalette.teal.default}40`
    : type === 'questionSet'
    ? `${theme.colors.brandPalette.orange.default}40`
    : `${theme.colors.neutralsPalette.grey}40`
  };

  ${ContentTypeText} {
    color: ${({theme, type}) => type === 'lesson'
      ? theme.colors.brandPalette.teal.default
      : type === 'questionSet'
      ? theme.colors.brandPalette.orange.default
      : theme.colors.neutralsPalette.grey
    };
  }
`;

const RowLink = styled(Link)`
  display: block;
  width: 100%;
  height: 100%;
  text-decoration: none;
  padding: 0px 8px;
  background: white;
  transition: all .2s;
  border-radius: 0px;
  border-bottom: 1px solid ${({theme}) => theme.colors.borderPalette.primary};

  ${({locked}) => locked && `
    &:hover {
      ${OptionContainer} {
        display: none;
      }
      ${BlockNumberContainer} {
        display: none;
      }
      ${RowContent} {
        display: none;
      }
      ${DetailText} {
        display: none;
      }
      ${UpgradeOverlayContainer} {
        display: flex;
      }
      ${BadgeWrapper} {
        margin-left: auto;
      }
      ${ContentTypeChicletContainer} {
        display: none;
      }
    }
  `}

  ${({theme}) => theme.mediaQueries.tablet} {
    padding: 6px 8px;
    display: flex;
    height: 60px;
  }

  ${({theme}) => theme.mediaQueries.mobileL} {
    ${ContentTypeChicletContainer} {
      display: none;
    }
  }


  &:hover {
    transform: scale(1.01);
    transition: transform .1s ease 0s;
    border-radius: 8px;
    background: ${({theme}) => theme.colors.neutralsPalette.extraLight};
    ${({dark}) => dark && css`
      background: ${({theme}) => theme.colors.neutralsPalette.light};
    `}
  }

  ${({dark}) => dark && css`
    background: ${({theme}) => theme.colors.neutralsPalette.light};

    ${RowTitle} {
      color: ${({theme}) => theme.colors.neutralsPalette.dark};
    }
`}

  ${({active}) => active && css`
    padding: 28px 34px;
    padding-left: 14px;
    background: white;
    border-radius: 8px;
    transition: all .3s;
    box-shadow: ${({theme}) => theme.elevation.shadow.medium};

    ${({theme}) => theme.mediaQueries.mobileL} {
      padding: 16px;
    }


    &:hover {
      background: white;
      box-shadow: ${({theme}) => theme.elevation.shadow.high};

      ${PlayButtonIcon} {
        transform: scale(1.05);
      }
    }

    ${BlockNumberContainer} {
      background: ${({theme, themePalette}) => themePalette ? themePalette.light : theme.colors.brandPalette.violet.light};
      border: 2px solid white;
      ${({theme}) => theme.mediaQueries.mobileL} {
        min-width: 32px;
        min-height: 32px;
      }

      ${BlockNumber} {
        color: ${({theme, themePalette}) => themePalette ? themePalette.dark : theme.colors.brandPalette.violet.dark}
      }
    }
    ${RowLabel} {
      display: block;
      ${({theme}) => theme.mediaQueries.mobileL} {
        display: block !important;
      }
    }
    ${RowTitle} {
      color: ${({theme}) => theme.colors.neutralsPalette.extraDark};
    }
    ${OptionOuter} {
      border-color: ${({theme}) => theme.colors.neutralsPalette.lightGrey};
    }
    ${PlayButtonIcon} {
      box-shadow: ${({theme}) => theme.elevation.shadow.medium};
      &:hover {
        background: ${({theme, themePalette}) => themePalette ? themePalette.hover : theme.colors.brandPalette.violet.hover};
      }
    }
    ${DetailText} {
      color: ${({theme}) => theme.colors.neutralsPalette.extraDark};
    }
  `}
`;

const BlockNumberContainer = styled(FlexBox)`
  align-items: center;
  justify-content: center;
  width: auto;
  border-radius: 8px;
  padding: 16px;
`;

const BlockNumber = styled(Label2)`
  width: 16px;
  height: 16px;
  font-weight: 600;
  text-align: center;
  color: ${({theme}) => theme.colors.typographyPalette.tertiary};
`;
const Duration = styled(Label2)`
  color: ${({theme}) => theme.colors.typographyPalette.secondary};
  ${({theme}) => theme.mediaQueries.tablet} {
    font-size: 14px;
  }
`;
const OptionHover = styled.div`
  line-height: 0;
  border-radius: 100px;
  transition: all 100ms;
  padding: 4px;
  position: relative;
  z-index: 1;
  &:hover {
    background: ${({theme}) => theme.colors.neutralsPalette.light};
  }
`;
const OptionBackground = styled.div`
  position: absolute;
  width: 16px;
  height: 16px;
  background: ${({theme, themePalette, checked}) => !checked ? 'none' : themePalette ? themePalette.default : theme.colors.brandPalette.violet.default};
  border-radius: 100px;
  z-index: -1;
`;
const OptionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;

  &:hover {
    ${OptionHover} {
      background: ${({theme}) => theme.colors.neutralsPalette.light};
    }
  }

  ${({theme}) => theme.mediaQueries.mobileL} {
    background: ${({theme}) => theme.colors.buttonPalette.primaryHover};
    border-radius: 100px;
    padding: 4px;
    margin-right: ${({theme}) => theme.layouts.spacing.s};
  }
`;
const IconWrapper = styled.div`
  display: flex;
  padding: 16px;
  margin-left: auto;
`;
const StyledChevronRight = styled(ChevronRight)`
  width: 16px;
  height: 16px;
  color: ${({theme}) => theme.colors.iconPalette.tertiary};
`;

const OptionOuter = styled.div`
  min-width: 16px;
  min-height: 16px;
  padding: 1px;
  border-radius: 100px;
  border: 1px solid ${({theme}) => theme.overlays.opacity.dark._200};
`;
const OptionInner = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 100px;
  background: none;
`;
const OptionCheckedWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
`;
const OptionChecked = styled(Checkmark)`
  width: 14px;
  height: 14px;
  color: white;
`;

const Option = ({checked, onClick, themePalette}) => checked
  ? <OptionCheckedWrapper>
      <OptionChecked onClick={onClick} themePalette={themePalette}/>
    </OptionCheckedWrapper>
  : <OptionOuter onClick={onClick} children={<OptionInner onClick={onClick}/>}/>

const UpgradeOverlayContainer = styled.div`
  position: absolute;
  display: none;
  z-index: 10;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${({theme}) => theme.overlays.opacity.light._100};
  color: white;
  align-items: center;
  justify-content: center;
`;
const UpgradeOverlayText = styled(H3)`
  color: ${({theme}) => theme.colors.neutralsPalette.grey};
`;
const ResourceIcon = styled(DocumentArrowDown)`
  width: 16px;
  height: 16px;
  color: ${({theme}) => theme.colors.iconPalette.tertiary};
`;
const QuizIcon = styled(TaskListSquareLtr)`
  width: 16px;
  height: 16px;
  color: ${({theme}) => theme.colors.iconPalette.tertiary};
`;
const StyledProgressBar = styled(ProgressBar)`
  width: 76px;
  margin-bottom: 0px;

  ${({theme}) => theme.mediaQueries.tablet} {
    flex: 1;
  }
`;

const UpgradeOverlay = () => (
  <UpgradeOverlayContainer>
    <UpgradeOverlayText>Upgrade to unlock</UpgradeOverlayText>
    <ArrowForwardOutline color={'#85868E'} size={20}/>
  </UpgradeOverlayContainer>
);

const TextBlockDetails = styled(Label2)`
  color: ${({theme}) => theme.colors.typographyPalette.tertiary};
`;

function getDuration(testBlock) {
  try {
    const json = findByRenderType(testBlock.components, 'video');
    const rawDuration = JSON.parse(json).duration;
    return parseVideoDuration(rawDuration);

  } catch(error) {
    return '';
  }
}

const iconMap = {
  'text': <ResourceIcon/>,
  'questionSet': <QuizIcon/>,
  'Quiz': <QuizIcon/>, // this is for bite block links
};

const RowDetails = ({testBlock, blockProgress, questionSetType}) => {
  const fetchMethod = blockProgress?.fetchMastery ? async => blockProgress?.fetchMastery(testBlock) : async () => Promise.resolve({});
  const fetchMethodCallback = useCallback(fetchMethod, [blockProgress?.fetchMastery]);
  const mastery = useAsync(fetchMethodCallback);

  const testBlockContentType = testBlock?.components?.find(({renderType}) => renderType === 'contentType')?.contents;

  if (testBlockContentType === 'Quiz') {
    const linkType = questionSetType === 'Quiz' ? 'QBank' : 'Bites';

    return (
      <TextBlockDetails>Link to {linkType}</TextBlockDetails>
    );
  } else if (testBlock?.type === 'text') {
    return (
      <TextBlockDetails>Download PDF</TextBlockDetails>
    );
  } else if (['questionSet', 'passage'].includes(testBlock?.type)) {
    const {value, pending} = mastery || {};
    const numQuestions = testBlock?.questionConnections?.items?.length;

    return (
      <QuizDetails>
        <QuizDetailsText loading={pending}>{value?.total} / {numQuestions}</QuizDetailsText>
        <StyledProgressBar
          segments={[
            {count: value?.mastered || 0},
            {count: value?.reviewing || 0},
            {count: value?.learning || 0},
          ]}
          total={numQuestions}
          showLabel={false}
          withMargin={false}
          loading={pending}
          />
      </QuizDetails>
    )
  } else if (testBlock?.type === 'lesson') {
    return (
      <Duration>{getDuration(testBlock)}</Duration>
    );
  } else {
    return null;
  }
};

const VideoRow = ({index, testBlock, blockProgress, saveBlockProgress, active, themePalette, locked, bootcamp, route, classroomName}) => {
  const {modalDispatch} = useModalContext();
  const {status} = blockProgress || {};
  const title = testBlock && testBlock.title;
  const type = testBlock?.type;
  const number = testBlock.type === 'text'
    ? findByRenderType(testBlock.components, 'contentType', 'Info')
    : findByRenderType(testBlock.components, 'number', ['questionSet', 'passage'].includes(testBlock?.type) ? 'Quiz' : '');

  const testBlockContentType = testBlock?.components?.find(({renderType}) => renderType === 'contentType')?.contents;

  const questionSetType = ['dat', 'oat'].includes(bootcamp) && classroomName !== 'Biology'
    ? 'Quiz'
    : 'Bites';

  const linkTo = locked ? `#` : `videos/${route}?index=${index}`;

  const blockIcon = iconMap?.[testBlockContentType] || iconMap?.[type]

  const onClick = !locked
    ? () => {}
    : ['oat', 'dat', 'inbde', 'anatomy'].includes(bootcamp)
    ? () => modalDispatch({
        type: 'open',
        component: Upgrade,
        enableClickClose: true,
        modalContainerStyle: theme => `
          background: ${theme.overlays.opacity.dark._300};
          padding: 10vh 10vw;
          align-items: flex-start;
          ${theme.mediaQueries.tablet} {
            background: ${theme.overlays.opacity.dark._400};
            padding: 0;
          }
        `
      })
    : () => window.location = `https://bootcamp.com/${bootcamp}/upgrade`;

  const onOptionClick = e => {
    e.persist();
    e.preventDefault();
    e.stopPropagation();
    saveBlockProgress({status: status === 'complete' ? '' : 'complete'}, index)
  };

  return (
    <RowLink themePalette={themePalette} onClick={onClick} to={linkTo} active={active} locked={locked}>
      <UpgradeOverlay/>
      <RowContainer>
        <OptionContainer onClick={onOptionClick}>
          <OptionHover>
            <OptionBackground themePalette={themePalette} checked={status === 'complete'}/>
            <Option checked={status === 'complete'} themePalette={themePalette} />
          </OptionHover>
        </OptionContainer>
        <BlockNumberContainer>
          {blockIcon
            ? blockIcon
            : <BlockNumber>{number}</BlockNumber>
          }
        </BlockNumberContainer>
        <ContentTypeChicletContainer>
          <ContentTypeChiclet type={type}>
            <ContentTypeText>
              {type === 'lesson' ? 'Video' : (['questionSet', 'passage'].includes(type) || ['Quiz'].includes(testBlockContentType)) ? questionSetType : 'PDF'}
            </ContentTypeText>
          </ContentTypeChiclet>
        </ContentTypeChicletContainer>
        <RowContent>
          <RowTitle>{title}</RowTitle>
          <RowDetails
            testBlock={testBlock}
            blockProgress={blockProgress}
            questionSetType={questionSetType}
          />
        </RowContent>
        {/* {locked
          ? (
            <IconArea>
              {numQuestions && <Duration>{numQuestions} question{numQuestions !== 1 ? 's' : ''}</Duration>}
              {duration && <Duration>{duration}</Duration>}
              <BadgeWrapper>
                <StyledBadge/>
              </BadgeWrapper>
            </IconArea>
          )
          : (
            <IconArea>
              {numQuestions && <Duration padded={numQuestions > 0}>{numQuestions} question{numQuestions !== 1 ? 's' : ''}</Duration>}
              {duration && <Duration>{duration}</Duration>}
              {!['text', 'questionSet'].includes(testBlock.type) && <PlayButtonIcon themePalette={themePalette}/>
              }
            </IconArea>
          )
        } */}
        <IconWrapper>
          {locked
            ? <BadgeWrapper>
                <StyledBadge/>
              </BadgeWrapper>
            : <StyledChevronRight/>
          }
        </IconWrapper>
      </RowContainer>
    </RowLink>
  )
}

export default VideoRow;
